import { LucideIcon } from 'lucide-react';

/**
 * The category of a page action — drives the palette icon fallback and lets the
 * discovery/registry reason about intent. `navigate` is used for pure in-page
 * cursor moves (e.g. next/prev conversation) which stay keyboard-only.
 */
export type PageActionCategory =
  | 'create'
  | 'update'
  | 'delete'
  | 'download'
  | 'sync'
  | 'navigate'
  | 'other';

/**
 * A single contextual page action declared by the active page — the ONE source
 * of truth behind both the palette's «На этой странице» section and the
 * closed-palette contextual keys + `?` cheat sheet.
 *
 * `run` reuses an existing page handler; `when` gates the action on a
 * role/permission/state (`false` hides + disables it). `key` is an OPTIONAL
 * single lowercase letter hotkey (a keyless action is click-only in the
 * palette). `showInPalette` (default `true`) controls whether the action shows
 * as a page-action row — pure keyboard-nav keys (e.g. Communicator `j`/`k`) set
 * it `false` so they stay keyboard-only + in the cheat sheet.
 */
export interface IPageAction {
  /** Stable id, unique within a page (React key + palette row key). */
  id: string;
  /** Optional single lowercase letter hotkey; unique per page; never `g`. */
  key?: string;
  label: string;
  icon?: LucideIcon;
  category?: PageActionCategory;
  run: () => void;
  when?: boolean;
  /** `false` = keyboard-only (not shown as a palette page-action row). */
  showInPalette?: boolean;
}

/**
 * @deprecated Kept as a back-compat alias while callers migrate to `IPageAction`
 * / `usePageActions`. Structurally identical.
 */
export type IPageShortcut = IPageAction;

/** Lazily reads a page's current action list (kept fresh via a ref). */
export type PageActionGetter = () => IPageAction[];

/** @deprecated alias of `PageActionGetter`. */
export type PageShortcutGetter = PageActionGetter;

export interface IPageShortcutsContext {
  register: (id: symbol, getter: PageActionGetter) => void;
  unregister: (id: symbol) => void;
  /** Flattened, live snapshot across every registered page. */
  getShortcuts: () => IPageAction[];
  /** Bumped on every mount/unmount so consumers (the cheat sheet) re-read. */
  version: number;
}

/** True when an action should render as a palette page-action row. */
export const isPaletteAction = (action: IPageAction): boolean =>
  action.showInPalette !== false;
